import { CsvCellValue } from "./types";

type DateOptions = Intl.DateTimeFormatOptions;

export const formatMillisecondsDate = (
  value: unknown,
  options?: DateOptions,
  emptyValue = ""
): string => formatDate(value, 1, options, emptyValue);

export const formatMillisecondsDateTime = (
  value: unknown,
  options?: DateOptions,
  emptyValue = ""
): string => formatDateTime(value, 1, options, emptyValue);

export const formatUnixSecondsDate = (
  value: unknown,
  options?: DateOptions,
  emptyValue = "-"
): string => formatDate(value, 1000, options, emptyValue);

export const formatUnixSecondsDateTime = (
  value: unknown,
  options?: DateOptions,
  emptyValue = "-"
): string => formatDateTime(value, 1000, options, emptyValue);

const formatDateTime = (
  value: unknown,
  multiplier: number,
  options: DateOptions | undefined,
  emptyValue: string
): string => {
  const date = toDate(value, multiplier);
  return date ? date.toLocaleString(undefined, options) : emptyValue;
};

const formatDate = (
  value: unknown,
  multiplier: number,
  options: DateOptions | undefined,
  emptyValue: string
): string => {
  const date = toDate(value, multiplier);
  return date ? date.toLocaleDateString(undefined, options) : emptyValue;
};

const toDate = (value: unknown, multiplier: number): Date | null => {
  if (value === null || value === undefined || value === "" || value === 0) {
    return null;
  }
  const numericValue = typeof value === "number" ? value * multiplier : value;
  const date = new Date(numericValue as string | number);
  return isNaN(date.getTime()) ? null : date;
};

export const formatInverseBoolean = (disabled: unknown): string =>
  disabled ? "No" : "Yes";

export const formatMemberStatus = (status: unknown): string => {
  if (typeof status !== "string" || !status) {
    return "";
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const formatOrgTags = (tags: unknown): string =>
  Array.isArray(tags) ? tags.filter(isCellValue).join("; ") : "";

export const formatDeviceSummary = (device: unknown): string => {
  if (!device || typeof device !== "object") {
    return "";
  }
  const value = device as {
    alias?: unknown;
    deviceShortId?: unknown;
    disabled?: unknown;
  };
  const alias = isCellValue(value.alias) ? String(value.alias) : "";
  const shortId = isCellValue(value.deviceShortId)
    ? String(value.deviceShortId)
    : "";
  const summary = [alias, shortId].filter(Boolean).join(": ");
  return summary && value.disabled ? `[Disabled] ${summary}` : summary;
};

export const formatPatientDevices = (devices: unknown): string =>
  Array.isArray(devices)
    ? devices.map(formatDeviceSummary).filter(Boolean).join("; ")
    : "";

export const formatDataRecency = (
  value: unknown,
  options: {
    isActive?: boolean;
    projectStartTime?: number | null;
    now?: Date;
  } = {}
): string => {
  if (options.isActive === false) {
    return "-";
  }
  if (
    value === undefined ||
    value === null ||
    (options.projectStartTime != null &&
      typeof value === "number" &&
      value < options.projectStartTime)
  ) {
    return "Never";
  }
  if (typeof value !== "number") {
    return "Never";
  }
  const daysAgo = Math.round(
    ((options.now || new Date()).getTime() - value * 1000) / 86400000
  );
  return `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;
};

const isCellValue = (
  value: unknown
): value is Exclude<CsvCellValue, null | undefined> =>
  value !== null && value !== undefined && value !== "";
